import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellOff, Check, Loader2, MailCheck } from "lucide-react";

/** Shared preference centre. `load()` → state, `save(body)` → state. Used by the dashboard tab (auth) and the signed-link page. */
export default function EmailPreferences({ load, save, kindLabel = "", compact = false }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");
  const [confirmAll, setConfirmAll] = useState(false);

  useEffect(() => {
    load().then(setState).catch((e) => setError(e.response?.data?.detail || "This link is no longer valid."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = async (key) => {
    const next = { ...state.prefs, [key]: !state.prefs[key] };
    setSaving(key);
    try {
      setState(await save({ prefs: next }));
      toast.success(next[key] ? "Turned on" : "Turned off — you won't get these any more");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving("");
    }
  };

  const unsubscribeAll = async () => {
    setSaving("all");
    try {
      setState(await save({ unsubscribe_all: true }));
      setConfirmAll(false);
      toast.success("Unsubscribed from everything");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't unsubscribe");
    } finally {
      setSaving("");
    }
  };

  if (error) return <div className="text-sm text-[#B35C4A]" data-testid="email-prefs-error">{error}</div>;
  if (!state) return <div className="text-sm text-[#7D8C85]" data-testid="email-prefs-loading">Loading…</div>;

  const hasGroups = state.type === "user" && state.groups?.length > 0;

  return (
    <div className={compact ? "" : "bg-white border border-[#E8ECE9] p-6"} data-testid="email-preferences">
      {state.email && <div className="text-xs text-[#7D8C85] mb-4" data-testid="email-prefs-email">Preferences for <span className="text-[#11211A]">{state.email}</span>{kindLabel ? <> · you received {kindLabel}</> : null}</div>}

      {state.unsubscribed_all ? (
        <div className="flex items-start gap-3 bg-[#F0EFEA] border-l-4 border-[#264839] p-4" data-testid="email-prefs-unsubscribed-banner">
          <BellOff className="w-5 h-5 text-[#264839] mt-0.5" />
          <div>
            <div className="font-medium text-[#11211A]">You're unsubscribed from everything</div>
            <div className="text-sm text-[#4A5550] mt-1">You won't receive any reminder or announcement emails from us. Account, password and quote-request emails still arrive. Changed your mind? Email <a href="mailto:hello@accommodationsuppliers.com" className="underline">hello@accommodationsuppliers.com</a>.</div>
          </div>
        </div>
      ) : (
        <>
          {hasGroups && (
            <div className="divide-y divide-[#E8ECE9]" data-testid="email-prefs-groups">
              {state.groups.map((g) => {
                const on = state.prefs[g.key] !== false;
                return (
                  <label key={g.key} className="flex items-start gap-3 py-3 cursor-pointer" data-testid={`email-pref-row-${g.key}`}>
                    <input type="checkbox" checked={on} disabled={saving === g.key} onChange={() => toggle(g.key)} className="mt-1 accent-[#264839] w-4 h-4" data-testid={`email-pref-toggle-${g.key}`} />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-[#11211A] flex items-center gap-2">{g.label}{saving === g.key && <Loader2 className="w-3.5 h-3.5 animate-spin text-[#7D8C85]" />}</div>
                      <div className="text-xs text-[#7D8C85] mt-0.5">{g.desc}</div>
                    </div>
                    <span className={`text-[11px] uppercase tracking-wider mt-1 ${on ? "text-[#264839]" : "text-[#7D8C85]"}`} data-testid={`email-pref-state-${g.key}`}>{on ? "On" : "Off"}</span>
                  </label>
                );
              })}
            </div>
          )}
          {!hasGroups && <p className="text-sm text-[#4A5550]" data-testid="email-prefs-no-groups">This address has no supplier account, so the only option is to stop all automated emails.</p>}

          <div className="mt-6 pt-5 border-t border-[#E8ECE9] flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-[#7D8C85] inline-flex items-center gap-1.5"><MailCheck className="w-3.5 h-3.5" /> Account, password and quote-request emails are never affected.</div>
            {confirmAll ? (
              <div className="flex items-center gap-2" data-testid="email-prefs-confirm-all">
                <span className="text-xs text-[#B35C4A]">Stop every reminder and announcement?</span>
                <button type="button" onClick={unsubscribeAll} disabled={saving === "all"} className="btn-primary text-xs inline-flex items-center gap-1 disabled:opacity-50" data-testid="email-prefs-unsubscribe-all-confirm">
                  {saving === "all" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Yes, unsubscribe
                </button>
                <button type="button" onClick={() => setConfirmAll(false)} className="text-xs text-[#7D8C85] hover:text-[#11211A]" data-testid="email-prefs-unsubscribe-all-cancel">Keep</button>
              </div>
            ) : (
              <button type="button" onClick={() => setConfirmAll(true)} className="text-xs text-[#B35C4A] hover:underline underline-offset-2 inline-flex items-center gap-1" data-testid="email-prefs-unsubscribe-all">
                <BellOff className="w-3.5 h-3.5" /> Unsubscribe from everything
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
